import clsx from 'clsx'
import { LinearGradient } from 'expo-linear-gradient'
import { useEffect, useState, type ReactNode } from 'react'
import { Animated, Pressable, View } from 'react-native'

import { TEST_ID } from '@/src/general/constants/testId'
import { colors } from '@/src/styles/colors'

const white = colors.primary['bg-light'].DEFAULT
const brown = colors.primary['text-contrast-default'].DEFAULT

type TGradientColors = readonly [string, string, ...string[]]
type TGradientStyles = {
  bgGradientColors: TGradientColors
  headGradientColors: TGradientColors
}

// READY FOR LINEAR GRADIENT
const defaultStyles: TGradientStyles = {
  bgGradientColors: ['transparent', 'transparent'],
  headGradientColors: [white, white],
}

const activeStyles: TGradientStyles = {
  bgGradientColors: [brown, brown],
  headGradientColors: [white, white],
}

export type TSwitchProps = {
  value?: boolean
  onValueChange?: (value: boolean) => void
  disabled?: boolean
  focusable?: boolean
  testID?: string
}

export const Switch = ({ value, onValueChange, focusable = true, disabled, testID }: TSwitchProps): ReactNode => {
  // oxlint-disable-next-line react/hook-use-state
  const [animatedValue] = useState(new Animated.Value(0))

  useEffect(() => {
    Animated.timing(animatedValue, {
      toValue: value ? 1 : 0,
      duration: 300,
      useNativeDriver: false,
    }).start()
  }, [animatedValue, value])

  const translateX = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [-2, 8],
  })

  const toggleSwitch = () => {
    if (disabled) return
    const isToggledOn = !value
    onValueChange?.(isToggledOn)
  }

  const currentStyles = value ? activeStyles : defaultStyles

  const switchContent = (
    <View
      testID={testID}
      accessible={true}
      role="checkbox"
      aria-checked={value}
      className={clsx(
        'pointer-events-none h-[14px] w-[24px] overflow-hidden rounded-xl  border-2 border-primary-text-contrast-default antialiased',
        !value && 'bg-transparent',
        value && 'bg-primary-text-contrast-default',
        !disabled && !value && 'opacity-60',
        disabled && value && 'opacity-50',
        disabled && !value && 'opacity-30',
      )}
    >
      <LinearGradient colors={currentStyles.bgGradientColors} className="flex-1 rounded-xl" start={{ x: 0, y: 0.5 }}>
        <View className="relative flex flex-1 flex-row items-center">
          <Animated.View
            testID={TEST_ID.MISCELLANEOUS.SWITCH_ANIMATED_VIEW}
            style={{
              transform: [{ translateX }],
            }}
          >
            <View className="h-[14px] w-[14px] items-center justify-center rounded-full bg-primary-text-contrast-default ">
              <LinearGradient colors={currentStyles.headGradientColors} className="h-[10px] w-[10px] rounded-[100px]" />
            </View>
          </Animated.View>
        </View>
      </LinearGradient>
    </View>
  )

  return focusable ? (
    <Pressable onPress={toggleSwitch} disabled={disabled}>
      {switchContent}
    </Pressable>
  ) : (
    switchContent
  )
}
